import { useCallback, useEffect, useMemo } from 'react';
import useFetchComments from './useFetchComments';
import useCollaborationSidebarState from './useCollaborationSidebarState';
import useIsCommentsDataStale from './useIsCommentsDataStale';
import useTrackLiveRefresh from './useTrackLiveRefresh';
import { createModuleRepliesMap } from '../utils/commentingUtils';
import { useRealtimeCommentingMessaging } from '../features/messaging/messageHandlers';
import { isUngated } from '../utils/auth';
import { REALTIME_COMMENTING_ENABLED } from '../constants/gates';
export const useRealtimeCommentHandler = ({
  objectType,
  objectId,
  callback
}) => {
  const isRealtimeCommentingEnabled = isUngated(REALTIME_COMMENTING_ENABLED);
  useRealtimeCommentingMessaging({
    skip: !isRealtimeCommentingEnabled,
    onReportNewComment: msg => {
      if (msg.payload.objectType !== objectType || msg.payload.objectId !== objectId) {
        return;
      }
      callback();
    }
  });
};
export default function useHasUnreadComments(objectType, objectId) {
  'use no memo';

  const {
    fetchComments,
    comments,
    loading,
    updateCache: updateCommentsCache
  } = useFetchComments();
  const {
    assetsLastUpdated,
    onUpdateCollaborationSidebarState
  } = useCollaborationSidebarState();
  const isCommentsDataStale = useIsCommentsDataStale({
    objectType,
    objectId,
    assetsLastUpdated
  });
  const fetchCommentsCallback = useCallback(stale => {
    fetchComments({
      objectType,
      objectId,
      refetch: stale || isCommentsDataStale
    });
  }, [fetchComments, objectId, objectType, isCommentsDataStale]);
  useEffect(() => {
    fetchCommentsCallback();
  }, [fetchComments, objectId, objectType, isCommentsDataStale, fetchCommentsCallback]);
  useRealtimeCommentHandler({
    objectType,
    callback: () => fetchCommentsCallback(true)
  });
  const unreadComments = useMemo(() => comments.filter(comment => !comment.read), [comments]);
  const unreadCommentsCount = useMemo(() => unreadComments.length, [unreadComments]);
  const hasUnreadComments = unreadCommentsCount > 0;
  const markCommentAsRead = useCallback(updatedCommentId => {
    if (!objectId || !objectType) {
      return;
    }
    updateCommentsCache({
      objectType,
      objectId,
      updatedCommentId
    });
  }, [objectId, objectType, updateCommentsCache]);
  useTrackLiveRefresh({
    unreadCommentsCount,
    unreadCommentsLoading: loading
  });
  useEffect(() => {
    onUpdateCollaborationSidebarState({
      unreadRepliesMap: createModuleRepliesMap(unreadComments)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadComments]);
  return {
    hasUnreadComments,
    unreadCommentsCount,
    refetchComments: () => fetchComments({
      objectType,
      objectId,
      refetch: true
    }),
    markCommentAsRead
  };
}